import { useState, type FormEvent } from 'react';
import type { Campaña } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { campanaService } from '../services/campanaService';
import { puedeOperarDinero } from '../utils/permissions';
import { fechaEnChile } from '../utils/dateHelpers';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { DatePicker } from './ui/DatePicker';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface Props {
  readonly isOpen: boolean; readonly onClose: () => void; readonly onSuccess: () => void;
  readonly campaña?: Campaña; readonly lumisialId: string; readonly mode: 'create' | 'edit';
}
export function CampanaModal(props: Props) {
  if (!props.isOpen) return null;
  return <FormularioCampana key={props.campaña?.id ?? props.lumisialId} {...props} />;
}
function FormularioCampana({ onClose, onSuccess, campaña, lumisialId, mode }: Omit<Props, 'isOpen'>) {
  const { usuario } = useAuth();
  const [nombre, setNombre] = useState(campaña?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(campaña?.descripcion ?? '');
  const [objetivo, setObjetivo] = useState(String(campaña?.objetivo_recaudacion ?? ''));
  const [inicio, setInicio] = useState(campaña?.fecha_inicio ?? fechaEnChile(new Date()) ?? '');
  const [fin, setFin] = useState(campaña?.fecha_fin ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const propio = puedeOperarDinero(usuario, { tipo: 'lumisial', id: lumisialId,
    diocesisId: null, provinciaEclesiasticaId: null });
  const abierta = !campaña || ['planificacion', 'en_curso'].includes(campaña.estado);
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const monto = Number(objetivo);
    if (!propio || !abierta || !nombre.trim() || !Number.isFinite(monto) || monto <= 0
      || (fin && fin < inicio)) { setError('Revisa el permiso, los datos y el estado de la convocatoria.'); return; }
    setSubmitting(true); setError(null);
    try {
      const datos = { nombre: nombre.trim(), descripcion: descripcion.trim(),
        objetivo_recaudacion: monto, fecha_inicio: inicio, fecha_fin: fin || undefined };
      if (mode === 'create') {
        await campanaService.create({ ...datos, lumisial_id: lumisialId, estado: 'planificacion',
          nivel_origen: 'local', objetivo_bloqueado: false, monto_recaudado: 0 });
      } else {
        if (!campaña) throw new Error('Falta la campaña.');
        const actual = await campanaService.getById(campaña.id);
        if (actual?.lumisial_id !== lumisialId || !['planificacion', 'en_curso'].includes(actual.estado)) {
          throw new Error('La convocatoria cambió.');
        }
        await campanaService.update(actual.id, { ...datos,
          objetivo_recaudacion: actual.objetivo_bloqueado ? actual.objetivo_recaudacion : monto });
      }
    } catch { setError('No pudimos guardar la campaña. Conservamos tus datos para revisarlos.'); setSubmitting(false); return; }
    setSubmitting(false); onClose(); onSuccess();
  };
  return <DialogShell isOpen onClose={onClose} loading={submitting}
    title={mode === 'create' ? 'Crear campaña local' : 'Editar campaña'}>
    <form onSubmit={guardar} className="space-y-4">
      <FormErrorAlert error={error} />
      {(!propio || !abierta) && <p>La junta responsable puede editar la convocatoria mientras esté abierta. La contabilidad pendiente continúa desde su detalle.</p>}
      <fieldset disabled={submitting || !propio || !abierta} className="space-y-4">
        <TextInput id="campana-nombre" label="Nombre" value={nombre} onChange={setNombre} required />
        <Textarea id="campana-descripcion" label="Descripción" value={descripcion} onChange={e => setDescripcion(e.target.value)} />
        <TextInput id="campana-objetivo" label="Objetivo de recaudación" type="number" value={objetivo}
          onChange={setObjetivo} min={0.01} step={0.01} required disabled={campaña?.objetivo_bloqueado} />
        {campaña?.objetivo_bloqueado && <p className="text-sm text-slate-600">La meta está fijada por la junta responsable de la campaña maestra.</p>}
        <DatePicker label="Fecha de inicio" value={inicio} onChange={setInicio} required />
        <DatePicker label="Fecha de fin" value={fin} onChange={setFin} min={inicio} />
      </fieldset>
      <p className="text-sm text-slate-600">Finalizar cierra nuevos compromisos y permite terminar cobros, correcciones, envíos y gastos pendientes.</p>
      <div className="flex justify-end gap-3"><Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button type="submit" isLoading={submitting} disabled={!propio || !abierta}>Guardar campaña</Button></div>
    </form>
  </DialogShell>;
}
